import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { formatErr } from "@/lib/api";

export function LoginPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success("Welcome back.");
      const dest = loc.state?.from || (u.role === "client" ? "/portal" : "/admin");
      nav(dest, { replace: true });
    } catch (err) { toast.error(formatErr(err)); } finally { setLoading(false); }
  };
  return <AuthShell title="Welcome back">
    <form onSubmit={submit} data-testid="login-form" className="space-y-4">
      <Input testid="login-email" type="email" required placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} />
      <Input testid="login-password" type="password" required placeholder="Password" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} />
      <button data-testid="login-submit" disabled={loading} className="btn-gold w-full">{loading ? "Signing in…" : "Sign in"}</button>
    </form>
    <div className="mt-6 p-4 rounded-xl bg-midnight/[0.04] border border-midnight/10">
      <div className="overline text-gold-dark text-[10px] mb-1">Broking clients</div>
      <p className="text-xs text-midnight/70 mb-2">Trade or manage your demat account?</p>
      <a href="https://www.angelone.in/login/" target="_blank" rel="noreferrer" data-testid="login-angelone" className="text-sm font-semibold text-midnight hover:text-gold-dark inline-flex items-center gap-1">
        Sign in to Angel One →
      </a>
    </div>
    <p className="mt-6 text-sm text-midnight/60">New client? <Link to="/register" className="text-gold-dark font-semibold">Create account</Link></p>
  </AuthShell>;
}

export function RegisterPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await register(form); toast.success("Account created."); nav("/portal", { replace: true }); }
    catch (err) { toast.error(formatErr(err)); } finally { setLoading(false); }
  };
  return <AuthShell title="Create your account">
    <form onSubmit={submit} data-testid="register-form" className="space-y-4">
      <Input testid="register-name" required placeholder="Full name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} />
      <Input testid="register-email" type="email" required placeholder="Email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} />
      <Input testid="register-phone" placeholder="Phone" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} />
      <Input testid="register-password" type="password" required placeholder="Password (min 6)" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} />
      <button data-testid="register-submit" disabled={loading} className="btn-gold w-full">{loading ? "Creating…" : "Create account"}</button>
    </form>
    <p className="mt-6 text-sm text-midnight/60">Have an account? <Link to="/login" className="text-gold-dark font-semibold">Sign in</Link></p>
  </AuthShell>;
}

function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-midnight/70 flex flex-col justify-between p-14 text-white">
          <Link to="/" className="inline-block bg-white/95 rounded-lg p-2 w-fit"><img src="/logo.webp" alt="Manigriv" className="h-10 w-auto" /></Link>
          <div><div className="overline text-gold mb-3">Fee-only advisory</div><h2 className="font-display text-4xl leading-tight">A quieter kind of wealth advice.</h2></div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="overline text-gold-dark mb-3">Client portal</div>
          <h1 className="font-display text-4xl text-midnight mb-8">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

function Input({ testid, ...props }) {
  return <input data-testid={testid} {...props} className="w-full px-4 py-3 rounded-lg border border-midnight/15 focus:border-gold outline-none text-sm" />;
}
